import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { getStaffById, getStaffPresenceToday, setPresenceOverride } from '../api/apiClient';
import './StaffDirectory.css';

const initials = name => name.trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();
const fmtPhone  = (phone, ext) => phone ? (ext ? `${phone} ext. ${ext}` : phone) : null;

// Presence is no longer colour-coded — one neutral Esbrook style for every status.
const ESB_NAVY = '#272e40';
const BIRTH_MONTH_FULL = { '01':'January','02':'February','03':'March','04':'April','05':'May','06':'June','07':'July','08':'August','09':'September','10':'October','11':'November','12':'December' };
const fmtBirthday = (b) => { const [m, d] = (b || '').split('-'); return (BIRTH_MONTH_FULL[m] && d) ? `${BIRTH_MONTH_FULL[m]} ${parseInt(d, 10)}` : null; };

const CakeIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 14h12v-4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z"/><path d="M2 11h12"/>
    <line x1="5.5" y1="4.5" x2="5.5" y2="6"/><line x1="8" y1="4.5" x2="8" y2="6"/><line x1="10.5" y1="4.5" x2="10.5" y2="6"/>
    <circle cx="5.5" cy="3.4" r="0.5"/><circle cx="8" cy="3.4" r="0.5"/><circle cx="10.5" cy="3.4" r="0.5"/>
  </svg>
);

const TeamsIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Right smaller person (behind, darker) */}
    <circle cx="19" cy="6" r="2.5" fill="#464EB8"/>
    <path d="M15.5 17c0-2 1.57-3.5 3.5-3.5s3.5 1.5 3.5 3.5z" fill="#464EB8"/>
    {/* Left larger person (lighter, partially behind badge) */}
    <circle cx="15.5" cy="5" r="3.5" fill="#7B83EB"/>
    <path d="M10.5 17c0-2.76 2.24-5 5-5s5 2.24 5 5z" fill="#7B83EB"/>
    {/* Teams badge */}
    <rect x="1" y="6" width="14" height="14" rx="3" fill="#5059C9"/>
    {/* T letter */}
    <path d="M4 10.5h8M8 10.5V17" stroke="white" strokeWidth="2.2" strokeLinecap="round"/>
  </svg>
);

const EmailIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="1" width="14" height="10" rx="2"/>
    <path d="M1 3l7 5 7-5"/>
  </svg>
);

const PhoneIcon = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.5 11.3l-2.8-1.2a1 1 0 0 0-1.1.23l-1.2 1.47A11 11 0 0 1 4.2 6.6L5.67 5.4a1 1 0 0 0 .23-1.1L4.7 1.5A1 1 0 0 0 3.6.93L1.4 1.5A1 1 0 0 0 .75 2.6 13.5 13.5 0 0 0 13.4 15.25a1 1 0 0 0 1.1-.65l.57-2.2a1 1 0 0 0-.57-1.1z"/>
  </svg>
);

const MobileIcon = () => (
  <svg width="10" height="12" viewBox="0 0 10 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="1" width="8" height="14" rx="2"/>
    <line x1="5" y1="12" x2="5" y2="12" strokeWidth="2"/>
  </svg>
);

const StaffProfilePopup = ({ staffId, onClose }) => {
  const [member, setMember] = useState(null);
  const [status, setStatus] = useState(null);
  const [canManage, setCanManage] = useState(false);
  const [saving, setSaving] = useState(false);
  const [awayMode, setAwayMode] = useState(false);
  const [returnDate, setReturnDate] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    getStaffById(staffId).then(res => setMember(res.data)).catch(() => onClose());
    getStaffPresenceToday(staffId).then(res => { setStatus(res.data.status); setCanManage(!!res.data.can_manage); }).catch(() => {});
  }, [staffId, onClose]);

  const applyStatus = async (newStatus, ooo = null) => {
    if (saving) return;
    setSaving(true);
    try {
      await setPresenceOverride({
        status: newStatus,
        out_of_office_until: newStatus === 'out' && ooo ? ooo : null,
        target_staff_id: staffId,
      });
      setStatus(newStatus);
      setAwayMode(false);
      setMember(m => m ? { ...m, out_of_office_until: newStatus === 'out' ? (ooo || null) : null } : m);
    } catch (err) {
      alert(err.response?.data?.error || 'Could not update status');
    } finally { setSaving(false); }
  };

  const onStatusBtn = (val) => {
    if (val === 'out') {
      setReturnDate(member?.out_of_office_until ? String(member.out_of_office_until).substring(0, 10) : '');
      setAwayMode(true);
    } else {
      setAwayMode(false);
      applyStatus(val);
    }
  };

  useEffect(() => {
    const down  = e => { if (e.key === 'Escape') onClose(); };
    const click = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('keydown', down);
    document.addEventListener('mousedown', click);
    return () => { document.removeEventListener('keydown', down); document.removeEventListener('mousedown', click); };
  }, [onClose]);

  const office = member ? fmtPhone(member.phone, member.phone_extension) : null;
  const oooUntil = member?.out_of_office_until
    ? String(member.out_of_office_until).substring(0, 10)
    : null;
  return ReactDOM.createPortal(
    <div className="sd-modal-overlay">
      <div className="sd-modal" ref={ref}>
        <button className="sd-modal-close" onClick={onClose}>✕</button>
        {!member ? (
          <p style={{ padding: '20px' }}>Loading…</p>
        ) : (
          <>
            <div className="sd-modal-top">
              <div className="sd-modal-avatar">
                {member.avatar
                  ? <img src={member.avatar} alt={member.name} />
                  : <span>{initials(member.name)}</span>}
              </div>
              <h2 className="sd-modal-name">{member.name}</h2>
              {member.title && <p className="sd-modal-title">{member.title}</p>}
              {member.office && <p className="sd-modal-location">{member.office}</p>}
              {status === 'out' && oooUntil && (
                <p className="sd-modal-status-return">
                  {`Back on ${new Date(oooUntil + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}`}
                </p>
              )}
            </div>

            <div className="sd-modal-body">
              {canManage && (
                <div className="sp-status-set">
                  <span className="sp-status-set-label">Set today's status</span>
                  <div className="sp-status-btns">
                    {[['office', 'In Office'], ['home', 'Remote'], ['out', 'Away']].map(([val, label]) => (
                      <button
                        key={val}
                        className={`sp-status-btn${(val === 'out' ? (status === 'out' || awayMode) : status === val) ? ' sp-status-btn--on' : ''}`}
                        style={(val === 'out' ? (status === 'out' || awayMode) : status === val) ? { borderColor: ESB_NAVY, color: ESB_NAVY, background: '#eef1f6' } : {}}
                        disabled={saving}
                        onClick={() => onStatusBtn(val)}
                      >{label}</button>
                    ))}
                  </div>
                  {awayMode && (
                    <div className="sp-away-row">
                      <label className="sp-away-label">Away until <span>(optional)</span></label>
                      <div className="sp-away-controls">
                        <input
                          type="date"
                          className="sp-away-input"
                          value={returnDate}
                          min={new Date().toLocaleDateString('en-CA')}
                          onChange={e => setReturnDate(e.target.value)}
                        />
                        <button className="sp-away-set" disabled={saving} onClick={() => applyStatus('out', returnDate || null)}>
                          {saving ? 'Saving…' : 'Set Away'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <a href={`mailto:${member.email}`} className="sd-modal-contact-row">
                <span className="sd-modal-contact-icon"><EmailIcon /></span>
                <span className="sd-modal-contact-text">{member.email}</span>
              </a>
              {office && (
                <div className="sd-modal-contact-row">
                  <span className="sd-modal-contact-icon"><PhoneIcon /></span>
                  <span className="sd-modal-contact-text">{office}</span>
                </div>
              )}
              {member.mobile_phone && (
                <div className="sd-modal-contact-row">
                  <span className="sd-modal-contact-icon"><MobileIcon /></span>
                  <span className="sd-modal-contact-text">{member.mobile_phone}</span>
                </div>
              )}
              {fmtBirthday(member.birthday) && (
                <div className="sd-modal-contact-row">
                  <span className="sd-modal-contact-icon"><CakeIcon /></span>
                  <span className="sd-modal-contact-text">{fmtBirthday(member.birthday)}</span>
                </div>
              )}
              <a
                href={`https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(member.email)}`}
                className="sd-modal-teams-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <TeamsIcon />
                Chat on Teams
              </a>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default StaffProfilePopup;
